import servedContract from './served_recommendation_v1.json' with { type: 'json' }

export const SERVED_CONTRACT = Object.freeze(servedContract)
export const SERVED_CONTRACT_VERSION = servedContract.contract_version
export const SERVED_CONTRACT_SHA256 = '4d11caf9d3596c80dff7e6be06503d7ea6542f90961f8c39636aa76e5d6829f6'
export const SERVED_CONTRACT_SHA = SERVED_CONTRACT_SHA256.slice(0, 12)

function surfaceConfig(surface) {
  const config = servedContract[`${surface}_surface`]
  if (!config) throw new RangeError(`Unknown served surface: ${surface}`)
  return config
}

function requiredClause(surface, predicate, description) {
  const clause = surfaceConfig(surface).filters.find(predicate)
  if (!clause) {
    throw new Error(`Served contract ${servedContract.contract_version} is missing ${description}`)
  }
  return clause
}

const QUERY_LINEAGE_FIELDS = Object.freeze([
  'decision_snapshot_id',
  'model_git_sha',
  'served_contract_version',
  'served_contract_sha',
])

function queryLineageClauses(surface) {
  return QUERY_LINEAGE_FIELDS.map(field => requiredClause(
    surface,
    clause => clause === `${field} != ''`,
    `non-empty ${field} clause`
  ))
}

const discoveryLineageClauses = queryLineageClauses('discovery')
const recommendationLineageClauses = queryLineageClauses('recommendation')
const queryLineageBySurface = Object.freeze({
  discovery: Object.freeze(discoveryLineageClauses),
  recommendation: Object.freeze(recommendationLineageClauses),
})

export const SERVED_CLAUSES = Object.freeze({
  positivePrice: requiredClause('discovery', clause => clause.startsWith('price_amount_eur >'), 'positive-price clause'),
  positiveRevenue: requiredClause('discovery', clause => clause.startsWith('expected_revenue >'), 'positive-revenue clause'),
  noPriceAnomaly: requiredClause('discovery', clause => clause.startsWith('price_flag_anomaly ='), 'price-anomaly clause'),
  releaseActive: requiredClause('discovery', clause => clause.startsWith('release_active ='), 'active-release clause'),
  queryLineage: queryLineageBySurface,
  notGone: requiredClause('discovery', clause => clause.startsWith('source_flag !='), 'liveness clause'),
  sourceLive: requiredClause('recommendation', clause => clause === "source_flag = 'live'", 'live-source clause'),
  sourceChecked: requiredClause('recommendation', clause => clause.includes('now - 24h'), 'source-check clause'),
  fresh: requiredClause('discovery', clause => clause.includes('now - 7d'), 'freshness clause'),
  positiveBuyScore: requiredClause('recommendation', clause => clause.startsWith('buy_score >'), 'recommendation clause'),
})

export const SERVED_DEFAULTS = Object.freeze({
  freshness: surfaceConfig('discovery').defaults.freshness,
  hideSold: surfaceConfig('discovery').defaults.hide_sold,
  sortBy: surfaceConfig('discovery').defaults.sort_by,
})

export const RELEASE_LINEAGE_FIELDS = Object.freeze([
  'source_checked_at',
  'decision_snapshot_id',
  'model_git_sha',
  'served_contract_version',
  'served_contract_sha',
])

export function missingRequiredFields(row) {
  return servedContract.required_fields.filter(field => !(field in row))
}

function hasNoValue(value) {
  return value == null || (typeof value === 'string' && value.trim() === '') ||
    (typeof value === 'number' && Number.isNaN(value))
}

export function releaseLineageIssues(row) {
  const issues = RELEASE_LINEAGE_FIELDS.filter(field => hasNoValue(row[field]))
  if (!issues.includes('served_contract_version') &&
      row.served_contract_version !== SERVED_CONTRACT_VERSION) {
    issues.push('served_contract_version_mismatch')
  }
  if (!issues.includes('served_contract_sha') && row.served_contract_sha !== SERVED_CONTRACT_SHA) {
    issues.push('served_contract_sha_mismatch')
  }
  return issues
}

export function servedSort(surface = 'discovery') {
  return surfaceConfig(surface).sort
}

export function formatUtcForPocketBase(value) {
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) throw new RangeError('Freshness cutoff must be a valid date')
  // PocketBase stores date strings with a space and millisecond precision.
  // A T sorts after a space and incorrectly excludes the cutoff day's rows.
  return date.toISOString().replace('T', ' ')
}

export function utcFreshnessClause(window, now = new Date()) {
  const current = now instanceof Date ? new Date(now.getTime()) : new Date(now)
  if (Number.isNaN(current.getTime())) throw new RangeError('Current time must be a valid date')

  let cutoff
  if (window === 'today') {
    cutoff = new Date(Date.UTC(current.getUTCFullYear(), current.getUTCMonth(), current.getUTCDate()))
  } else {
    const durations = {
      '1h': 60 * 60 * 1000,
      '6h': 6 * 60 * 60 * 1000,
      '24h': 24 * 60 * 60 * 1000,
      '7d': 7 * 24 * 60 * 60 * 1000,
    }
    if (!durations[window]) return null
    cutoff = new Date(current.getTime() - durations[window])
  }

  const field = SERVED_CLAUSES.fresh.split('>=', 1)[0].trim()
  return `${field} >= '${formatUtcForPocketBase(cutoff)}'`
}

export function utcSourceCheckClause(now = new Date()) {
  const current = now instanceof Date ? new Date(now.getTime()) : new Date(now)
  if (Number.isNaN(current.getTime())) throw new RangeError('Current time must be a valid date')
  const hours = Number(surfaceConfig('recommendation').source_check_max_age_hours)
  if (!Number.isFinite(hours) || hours <= 0) {
    throw new RangeError('Recommendation source-check age must be a positive number')
  }
  const cutoff = new Date(current.getTime() - hours * 60 * 60 * 1000)
  const field = SERVED_CLAUSES.sourceChecked.split('>=', 1)[0].trim()
  return `${field} >= '${formatUtcForPocketBase(cutoff)}'`
}

export function servedFilter(surface = 'discovery', now = new Date()) {
  return surfaceConfig(surface).filters
    .map(clause => {
      if (clause === SERVED_CLAUSES.fresh) {
        return utcFreshnessClause(SERVED_DEFAULTS.freshness, now)
      }
      if (clause === SERVED_CLAUSES.sourceChecked) return utcSourceCheckClause(now)
      return clause
    })
    .join(' && ')
}

export function servedContractProvenance() {
  return Object.freeze({
    contractVersion: SERVED_CONTRACT_VERSION,
    contractSha: SERVED_CONTRACT_SHA,
    servedBackend: servedContract.served_backend.public_base_url,
    frontendContractUrl: servedContract.served_frontend.contract_url,
    collection: servedContract.served_backend.collection,
  })
}
